import { useState, useEffect } from 'react';
import { FileText, Filter, Download, Search } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('all');
  const [limit, setLimit] = useState(50);

  useEffect(() => {
    fetchLogs();
  }, [limit, actionFilter]);

  const fetchLogs = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams();
      
      if (limit) params.append('limit', limit);
      if (actionFilter !== 'all') params.append('action', actionFilter);
      
      const response = await api.get(`/admin/audit-logs?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Map backend response to frontend format
      const mappedLogs = (response.data.logs || []).map(log => ({
        id: log.action_id,
        action: log.action_type,
        admin_email: log.admin_email,
        admin_name: log.admin_email?.split('@')[0] || 'Admin',
        details: log.description,
        ip_address: log.ip_address,
        timestamp: log.timestamp
      }));
      
      setLogs(mappedLogs);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      toast.error('Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  };

  const getActionColor = (action) => {
    if (action.includes('approve') || action.includes('create')) return 'text-green-400 bg-green-500/20';
    if (action.includes('reject') || action.includes('delete')) return 'text-red-400 bg-red-500/20';
    if (action.includes('update') || action.includes('edit')) return 'text-yellow-400 bg-yellow-500/20';
    return 'text-blue-400 bg-blue-500/20';
  };

  const filteredLogs = logs.filter(log => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      log.action?.toLowerCase().includes(search) ||
      log.admin_email?.toLowerCase().includes(search) ||
      log.details?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white">Audit Logs</h1>
          <p className="text-gray-400 text-sm">Track all admin activities</p>
        </div>
        <div className="flex items-center space-x-3">
          <div className="bg-purple-600/20 px-3 sm:px-4 py-2 rounded-lg border border-purple-500/30">
            <p className="text-purple-400 font-semibold text-sm">{logs.length} Logs</p>
          </div>
          <button className="flex items-center space-x-2 px-3 sm:px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white font-semibold text-sm">
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">Export</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Search */}
        <div className="sm:col-span-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search logs..."
              className="w-full pl-10 pr-4 py-2 bg-gray-900 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 border border-gray-700 text-sm"
            />
          </div>
        </div>

        {/* Action Filter */}
        <div>
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="w-full px-4 py-2 bg-gray-900 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 border border-gray-700 text-sm"
          >
            <option value="all">All Actions</option>
            <option value="deposit">Deposits</option>
            <option value="withdrawal">Withdrawals</option>
            <option value="kyc">KYC</option>
            <option value="user">User Management</option>
            <option value="admin">Admin Management</option>
          </select>
        </div>
      </div>

      {/* Logs List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
        </div>
      ) : filteredLogs.length === 0 ? (
        <div className="bg-gray-900 rounded-xl p-12 text-center border border-gray-800">
          <FileText className="h-12 w-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">No audit logs found</p>
        </div>
      ) : (
        <>
          {/* Desktop View */}
          <div className="hidden md:block bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-400 uppercase">Timestamp</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-400 uppercase">Admin</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-400 uppercase">Action</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-400 uppercase">Details</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-400 uppercase">IP Address</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {filteredLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-800/50">
                      <td className="px-6 py-4">
                        <p className="text-white text-sm">
                          {new Date(log.timestamp).toLocaleString()}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-white font-semibold text-sm">{log.admin_name || 'N/A'}</p>
                          <p className="text-gray-400 text-xs">{log.admin_email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getActionColor(log.action)}`}>
                          {log.action}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-gray-300 text-sm">{log.details || 'N/A'}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-gray-400 text-sm font-mono">{log.ip_address || 'N/A'}</p>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile View */}
          <div className="md:hidden space-y-3">
            {filteredLogs.map((log) => (
              <div key={log.id} className="bg-gray-900 rounded-xl p-4 border border-gray-800">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="text-white font-bold text-sm">{log.admin_name || 'N/A'}</p>
                    <p className="text-gray-400 text-xs">{log.admin_email}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getActionColor(log.action)}`}>
                    {log.action}
                  </span>
                </div>
                <p className="text-gray-300 text-sm mb-2">{log.details || 'N/A'}</p>
                <div className="flex justify-between items-center">
                  <p className="text-gray-400 text-xs">{new Date(log.timestamp).toLocaleString()}</p>
                  <p className="text-gray-400 text-xs font-mono">{log.ip_address || 'N/A'}</p>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Load More */}
      {logs.length >= limit && (
        <div className="text-center">
          <button
            onClick={() => setLimit(limit + 50)}
            className="px-6 py-3 bg-purple-600 hover:bg-purple-700 rounded-lg text-white font-semibold"
          >
            Load More
          </button>
        </div>
      )}
    </div>
  );
};

export default AuditLogs;
